// src/pages/SettingsPage/components/WorkspaceUserTable.tsx
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import {
  Box,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  useTheme,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { NoteBlockContainer } from "../../note";
import { useAppId } from "../../../hook";
import { useWorkspaceUsers, useUserIsAppOwner, useFetchWorkspaceUsers, useAddWorkspaceUser, useDeleteWorkspaceUser, useCanManageMembers } from "../../../hook/settings";
import { initialWorkspaceUserFormData } from "./settings.contants";
import { WorkspaceUserFormData } from "./types/settings.types";
import { AddWorkspaceUserModal } from "./AddWorkSpaceUserModal";
import { StaticHtmlBlock } from "../../staticHtmlBlock";

export const WorkspaceUserTable: React.FC = () => {
  const [appId, setAppId] = useAppId();
  const [workspaceUsers, setWorkspaceUsers] = useWorkspaceUsers();
  const userIsAppOwnerHook = useUserIsAppOwner();
  const canManageMembers = useCanManageMembers();
  const fetchWorkspaceUsers = useFetchWorkspaceUsers();
  const theme = useTheme();
  const addWorkspaceUser = useAddWorkspaceUser();
  const deleteWorkspaceUser = useDeleteWorkspaceUser();
  const [addUserModalOpen, setAddUserModelOpen] = useState(false);
  const [formData, setFormData] = useState<WorkspaceUserFormData>(
    initialWorkspaceUserFormData
  );

  useEffect(() => {
    if (!appId) return;
    setWorkspaceUsers([]);
    fetchWorkspaceUsers({ appId });
  }, [appId]);

  return (
    <>
      <AddWorkspaceUserModal
        appId={appId!}
        formData={formData}
        setFormData={setFormData}
        modalOpen={addUserModalOpen}
        setModelOpen={setAddUserModelOpen}
        onSubmit={({ formData: { email, name }, appId: modalAppId }) => {
          addWorkspaceUser({ email, name, appId: modalAppId ?? appId! });
        }}
      />
      <NoteBlockContainer name="Workspace Users">
        <StaticHtmlBlock html="<p>All members of this workspace are listed below. Add or remove members to control who has access.</p>" />
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Email</TableCell>
              <TableCell>Name</TableCell>
              <TableCell></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {workspaceUsers?.map((workspaceUser) => {
              return (
                <TableRow key={workspaceUser.email}>
                  <TableCell>{workspaceUser.email}</TableCell>
                  <TableCell>{workspaceUser.name}</TableCell>
                  <TableCell>
                    {canManageMembers && (
                      <IconButton
                        onClick={() =>
                          deleteWorkspaceUser({
                            appId: appId!,
                            email: workspaceUser.email,
                          })
                        }
                        disabled={userIsAppOwnerHook({
                          email: workspaceUser.email,
                        })}
                        sx={{ color: theme.palette.primary.main }}
                        aria-label={`Remove ${workspaceUser.email}`}
                      >
                        <DeleteIcon />
                      </IconButton>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
        {canManageMembers && (
          <Box sx={{ justifyContent: "end", display: "flex" }}>
            <IconButton
              onClick={() => setAddUserModelOpen(true)}
              sx={{ color: theme.palette.primary.main }}
              aria-label="Add workspace user"
            >
              <AddIcon />
            </IconButton>
          </Box>
        )}
      </NoteBlockContainer>
    </>
  );
};
